import { keepPreviousData, useQuery } from '@tanstack/vue-query'
import type {
  Schemas,
  SchemaAttributes,
  ResponseByPath,
  QueryParamsByPath,
} from '@@/api/types'
import { useApi } from '@/composables/useApi.js'
import type {
  DepartmentFilter,
  SearchQueryParams,
} from '@/composables/globalSearch.js'

// --- API RESPONSE TYPES (what comes from the server) ---
const GLOBAL_SEARCH_PATH_V3 = '/{provet_id}/api/search/search_v3'
export type NavigationSearchResultResponse = ResponseByPath<
  'get',
  typeof GLOBAL_SEARCH_PATH_V3
>
export type NavigationSearchResultQueryParams = QueryParamsByPath<
  'get',
  typeof GLOBAL_SEARCH_PATH_V3
>

export type NavigationSearchResultType = NonNullable<
  NavigationSearchResultQueryParams['filter[result_type.in]']
>

export type ItemNavigationSearchData = Schemas['ItemNavigationSearch']
export type BundleNavigationSearchData = Schemas['BundleNavigationSearch']

export const ITEM_TYPE_CODES: Record<
  string,
  ItemNavigationSearchData['type_code']
> = {
  PROCEDURE: 1,
  MEDICINE: 2,
  SUPPLY: 3,
  FOOD: 4,
  LABORATORY_ANALYSIS: 5,
  LABORATORY_ANALYSIS_PANEL: 6,
} as const

export type NavigationSearchResult = SchemaAttributes<'NavigationSearchResult'>

export type NavigationSearchPatientDataItem =
  Schemas['NestedPatientNavigationSearch']

export type NavigationSearchClientDataItem =
  Schemas['ClientWithPatientNavigationSearch']

export type NavigationSearchClientWithPatientData =
  Schemas['NavigationSearchClientWithPatient']['data']

export type NavigationSearchItem = Schemas['NavigationSearchItem']
export type NavigationSearchItemData = SchemaAttributes<'SearchResult'> & {
  extra: ItemNavigationSearchData
}

export type NavigationSearchBundle = Schemas['NavigationSearchBundle']
export type NavigationSearchBundleData = SchemaAttributes<'SearchResult'> & {
  extra: BundleNavigationSearchData
}

export type NavigationSearchResultAttributes =
  | NavigationSearchClientWithPatientData
  | NavigationSearchItemData
  | NavigationSearchBundleData

export const NavigationSearchResultDataTypes: Record<
  string,
  NavigationSearchResultType[number]
> = {
  Client: 'CLIENT',
  Patient: 'PATIENT',
  Item: 'ITEM',
  Bundle: 'BUNDLE',
  ClientPatient: 'CLIENT_PATIENT',
} as const

// Re-export from models for backward compatibility
export {
  ResultItemModalType,
  type ResultItemModalTypeValue,
} from '@/models/navigationSearch.js'

const globalSearchKeys = {
  all: ['searchResults'] as const,
  searches: () => [...globalSearchKeys.all, 'list'] as const,
  search: (query: MaybeRef<SearchQueryParams>) =>
    [...globalSearchKeys.searches(), query] as const,
}

type SelectFunctionType<TData> = (
  response: NavigationSearchResultResponse,
) => TData
export function useNavigationSearchQuery<
  TQueryResponse = NavigationSearchResultResponse,
>(
  filters: {
    search: MaybeRef<string>
    resultTypes?: MaybeRef<NavigationSearchResultType>
    departmentFilter?: MaybeRef<DepartmentFilter | undefined>
    pageNumber?: MaybeRef<number>
    pageSize?: MaybeRef<number>
  },
  {
    enabled,
    select,
    keepPrevious,
  }: {
    enabled: MaybeRef<boolean>
    select?: SelectFunctionType<TQueryResponse>
    keepPrevious?: boolean
  },
) {
  const api = useApi()
  const route = useRoute()

  const queryParams = computed<SearchQueryParams>(() => {
    const search = unref(filters.search).trim()
    const resultTypes = unref(filters.resultTypes)
    const departmentFilter = unref(filters.departmentFilter)
    const pageNumber = unref(filters.pageNumber) || 1
    const pageSize = unref(filters.pageSize) || 50

    const thisDepartment = departmentFilter?.type === 'this' ? true : undefined
    const departmentGroupId =
      departmentFilter?.type === 'group'
        ? departmentFilter.departmentGroupId
        : undefined

    return {
      'filter[search]': search,
      'filter[result_type.in]': resultTypes,
      'filter[this_department]': thisDepartment,
      'filter[department_group_id]': departmentGroupId,
      'page[number]': pageNumber,
      'page[size]': pageSize,
    }
  })

  const query = useQuery({
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Immediately garbage collect when query becomes inactive.
    // Each search term creates a unique cache key that's never reused
    // (search resets on close), so caching provides no benefit.
    gcTime: 0,
    queryKey: globalSearchKeys.search(queryParams),
    queryFn: () =>
      api((client) =>
        client.GET(GLOBAL_SEARCH_PATH_V3, {
          params: {
            path: { provet_id: route.params.provetId },
            query: queryParams.value,
          },
        }),
      ),
    ...(select ? { select } : {}),
    ...(keepPrevious ? { placeholderData: keepPreviousData } : {}),
  })

  return reactive(query)
}
